import { GlobalAlert } from '@thoughtspot/global-alert';
import { useTranslation } from '@thoughtspot/i18n';
import { Alert, BannerAlertType, } from '@thoughtspot/radiant-react/widgets/alert';
import { Horizontal, Vertical, } from '@thoughtspot/radiant-react/widgets/layout';
import { Modal, ModalSize } from '@thoughtspot/radiant-react/widgets/modal';
import React, { useEffect, useMemo, useState } from 'react';
import { TranslationsKeys } from '../../../../../authentication/constants';
import { useAuthSettingsVisibility, useScimToken } from '../../../shared';
import styles from './all-orgs-settings-modal.module.scss';
import { LocalAuthContent } from './local-auth-content.container';
import { ScimSection } from './scim-section';
const ALL_SIDEBAR_TABS = [
    {
        tab: 'scim',
        translationKey: TranslationsKeys.SETTINGS_SIDEBAR_SCIM_PROVISIONING,
        testId: 'sidebar-scim',
    },
    {
        tab: 'localAuth',
        translationKey: TranslationsKeys.SETTINGS_SIDEBAR_LOCAL_AUTH,
        testId: 'sidebar-local-auth',
    },
    {
        tab: 'mfaSettings',
        translationKey: TranslationsKeys.SETTINGS_SIDEBAR_MFA_SETTINGS,
        testId: 'sidebar-mfa-settings',
    },
];
export const AllOrgsSettingsModal = ({ onClose, onSave, initialTab = 'scim', }) => {
    const { t } = useTranslation();
    const { showScim, showLocalAuth, showMfa } = useAuthSettingsVisibility();
    const visibleTabs = useMemo(() => ALL_SIDEBAR_TABS.filter(({ tab }) => {
        if (tab === 'scim')
            return showScim;
        if (tab === 'localAuth')
            return showLocalAuth;
        return showMfa;
    }), [showScim, showLocalAuth, showMfa]);
    const fallbackTab = visibleTabs[0]?.tab ?? 'scim';
    const requestedTabVisible = visibleTabs.some(entry => entry.tab === initialTab);
    const [activeTab, setActiveTab] = useState(requestedTabVisible ? initialTab : fallbackTab);
    useEffect(() => {
        if (!visibleTabs.some(entry => entry.tab === activeTab) &&
            visibleTabs.length) {
            setActiveTab(visibleTabs[0].tab);
        }
    }, [visibleTabs, activeTab]);
    const scim = useScimToken({ t });
    const close = () => scim.closeWithSavedToast(onClose);
    return (React.createElement(React.Fragment, null,
        React.createElement(Modal, { isOpen: true, onDismiss: close, size: ModalSize.MEDIUM3, title: t(TranslationsKeys.SETTINGS_MODAL_TITLE), className: styles.allOrgsModal, customHeaderClassName: styles.allOrgsModalHeader, confirmButton: {
                text: t('CLOSE'),
                onConfirm: () => scim.closeWithSavedToast(onSave),
            }, isContentLoading: scim.isInitialFetchLoading || scim.isMutationInFlight, "data-testid": "all-orgs-settings-modal" },
            React.createElement(Horizontal, { className: styles.allOrgsModalBody },
                React.createElement(Vertical, { className: styles.allOrgsSidebar }, visibleTabs.map(({ tab, translationKey, testId }) => {
                    const isActive = activeTab === tab;
                    return (React.createElement("div", { key: tab, className: isActive
                            ? styles.allOrgsSidebarItemBoxShadow
                            : undefined },
                        React.createElement("div", { role: "button", tabIndex: 0, "data-testid": testId, className: `${styles.allOrgsSidebarItem} ${isActive
                                ? styles.allOrgsSidebarItemActive
                                : ''}`, onClick: () => setActiveTab(tab), onKeyDown: e => {
                                if (e.key === 'Enter' ||
                                    e.key === ' ') {
                                    e.preventDefault();
                                    setActiveTab(tab);
                                }
                            } }, t(translationKey))));
                })),
                activeTab === 'scim' && (React.createElement(Vertical, { className: styles.allOrgsContent, spacing: "f" },
                    React.createElement(ScimSection, { scim: scim, inlineLearnMore: true }))),
                activeTab === 'localAuth' && (React.createElement(Vertical, { className: styles.allOrgsContent, spacing: "c" },
                    React.createElement(Alert.Banner, { message: t(TranslationsKeys.INTERNAL_AUTH_FORM_CONTENT), isVisible: true, type: BannerAlertType.WARNING, showCloseButton: false, className: styles.allOrgsWarningBanner }),
                    React.createElement(LocalAuthContent, { view: "localAuth", onSettingChanged: scim.markSettingsChanged, onSuccess: scim.notifySuccess, onError: scim.notifyError }))),
                activeTab === 'mfaSettings' && (React.createElement(Vertical, { className: styles.allOrgsContent, spacing: "c" },
                    React.createElement(LocalAuthContent, { view: "mfaSettings", onSettingChanged: scim.markSettingsChanged, onSuccess: scim.notifySuccess, onError: scim.notifyError }))))),
        React.createElement(GlobalAlert.Toast, { "data-testid": "all-orgs-settings-toast", message: scim.toastMessage, isVisible: scim.isToastVisible, onClose: scim.hideToast, autoHide: true })));
};
//# sourceMappingURL=all-orgs-settings-modal.container.js.map